import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { StatusBadge } from '@/components/ui/Badge';

const MATERIAL_SPECS = ['A36', 'A572-50', 'A500-B', 'A992', 'A53-B', 'A500-C', 'Misc'];
const STAGE_NAMES    = ['Detail', 'Material Cut', 'Fit-Up', 'Weld', 'Grind/Clean', 'Paint', 'Final QC', 'Ship'];

function AddJobModal({ onClose }) {
  const { addJob, customers, openModal } = useApp();
  const [form, setForm] = useState({
    jobNumber: `J-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    customerId: '',
    projectName: '',
    poNumber: '',
    dueDate: '',
    estTons: '',
    status: 'active',
    notes: '',
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.jobNumber.trim() || !form.projectName.trim()) return;
    addJob({ ...form, customerId: form.customerId ? Number(form.customerId) : null, estTons: parseFloat(form.estTons) || null });
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">New Job</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="field">
                <label>Job Number *</label>
                <input value={form.jobNumber} onChange={e => set('jobNumber', e.target.value)} required />
              </div>
              <div className="field">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="quote">Quote</option>
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label>Project Name *</label>
              <input value={form.projectName} onChange={e => set('projectName', e.target.value)} placeholder="e.g. Pinnacle Office Complex — Structural Steel" required />
            </div>
            <div className="form-row">
              <div className="field">
                <label>Customer / GC</label>
                <select value={form.customerId} onChange={e => set('customerId', e.target.value)}>
                  <option value="">Select customer…</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>PO Number</label>
                <input value={form.poNumber} onChange={e => set('poNumber', e.target.value)} placeholder="e.g. GC-2025-0042" />
              </div>
            </div>
            <div className="form-row">
              <div className="field">
                <label>Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
              </div>
              <div className="field">
                <label>Estimated Tons</label>
                <input type="number" step="0.1" min="0" value={form.estTons} onChange={e => set('estTons', e.target.value)} placeholder="e.g. 12.5" />
              </div>
            </div>
            <div className="field">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Scope, special requirements…" />
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Job</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddPieceMarkModal({ jobId, assemblyId, onClose }) {
  const { addPieceMark, workCenters, fabStages } = useApp();
  const [form, setForm] = useState({ markNumber: '', description: '', qty: 1, materialSpec: 'A36', workCenterId: workCenters[0]?.id || '', notes: '' });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.markNumber.trim()) return;
    addPieceMark({
      ...form,
      qty: parseInt(form.qty) || 1,
      jobId,
      assemblyId,
      workCenterId: form.workCenterId ? Number(form.workCenterId) : null,
      currentStageId: 1,
    });
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">Add Piece Mark</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="field">
                <label>Mark Number *</label>
                <input value={form.markNumber} onChange={e => set('markNumber', e.target.value)} placeholder="e.g. C1, B1, ST-1" required />
              </div>
              <div className="field">
                <label>Qty</label>
                <input type="number" min="1" value={form.qty} onChange={e => set('qty', e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. W8x31 Column" />
            </div>
            <div className="form-row">
              <div className="field">
                <label>Material Spec</label>
                <select value={form.materialSpec} onChange={e => set('materialSpec', e.target.value)}>
                  {MATERIAL_SPECS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Work Center</label>
                <select value={form.workCenterId} onChange={e => set('workCenterId', e.target.value)}>
                  <option value="">Unassigned</option>
                  {workCenters.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Mark</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdvanceStageModal({ pieceMark, onClose }) {
  const { advanceStage, fabStages } = useApp();
  const currentStage = fabStages.find(s => s.id === pieceMark.currentStageId);
  const nextStage    = fabStages.find(s => s.id === (pieceMark.currentStageId || 0) + 1);
  const [note, setNote] = useState('');

  const handleAdvance = () => {
    if (!nextStage) return;
    advanceStage(pieceMark.id, nextStage.id, { note });
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">Advance Stage — {pieceMark.markNumber}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0 16px', borderBottom: '1px solid var(--b1)', marginBottom: 14 }}>
            <span className="bdg" style={{ background: 'var(--s3)', color: 'var(--t3)' }}>{currentStage?.name || 'Unknown'}</span>
            <span style={{ color: 'var(--t4)' }}>→</span>
            <span className="bdg" style={{ background: 'var(--accent-t)', color: 'var(--accent)', fontWeight: 700 }}>{nextStage?.name || 'Shipped'}</span>
          </div>
          {nextStage ? (
            <>
              <div className="field">
                <label>Note (optional)</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Any inspection notes, issues, or comments…" style={{ minHeight: 60 }} />
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--t3)', fontSize: 13 }}>This piece mark is already at the final stage.</div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {nextStage && <button className="btn btn-accent" onClick={handleAdvance}>Advance to {nextStage.name}</button>}
        </div>
      </div>
    </div>
  );
}

export default function Jobs() {
  const { jobs, assemblies, pieceMarks, customers, fabStages, workCenters, custById, stageById } = useApp();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [modal, setModal] = useState(null); // {type, data}

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      const cust = custById(j.customerId);
      const matchSearch = !search ||
        j.jobNumber?.toLowerCase().includes(search.toLowerCase()) ||
        j.projectName?.toLowerCase().includes(search.toLowerCase()) ||
        cust?.name?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || j.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [jobs, search, statusFilter, custById]);

  const selectedJob = jobs.find(j => j.id === selectedJobId);

  const jobAssemblies = useMemo(() =>
    assemblies.filter(a => a.jobId === selectedJobId),
    [assemblies, selectedJobId]
  );

  const jobPieceMarks = useMemo(() =>
    pieceMarks.filter(p => p.jobId === selectedJobId),
    [pieceMarks, selectedJobId]
  );

  const jobProgress = useMemo(() => {
    if (!selectedJob) return 0;
    const marks = jobPieceMarks;
    if (!marks.length) return 0;
    const shipped = marks.filter(p => p.currentStageId === 8 || p.status === 'complete').length;
    return Math.round((shipped / marks.length) * 100);
  }, [selectedJob, jobPieceMarks]);

  const stageColor = (id) => {
    const colors = { 1:'#7c3aed', 2:'#0891b2', 3:'#d97706', 4:'#f97316', 5:'#059669', 6:'#be185d', 7:'#dc2626', 8:'#1e3a5f' };
    return colors[id] || '#94a3b8';
  };

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Jobs</div>
          <div className="psub">{jobs.filter(j => j.status === 'active').length} active · {jobs.filter(j => j.status === 'quote').length} in quote</div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary" onClick={() => setModal({ type: 'add_job' })}>+ New Job</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 180px)', overflow: 'hidden' }}>
        {/* Job list */}
        <div className="card" style={{ width: 380, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--b1)', display: 'flex', gap: 8 }}>
            <div className="search-bar" style={{ flex: 1 }}>
              <span>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs…" />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 8, padding: '0 10px', fontSize: 12, color: 'var(--t1)', fontFamily: 'inherit', cursor: 'pointer' }}
            >
              <option value="all">All</option>
              <option value="quote">Quote</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="complete">Complete</option>
              <option value="invoiced">Invoiced</option>
            </select>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                No jobs found — create your first job to get started.
              </div>
            )}
            {filtered.map(job => {
              const cust = custById(job.customerId);
              const marks = pieceMarks.filter(p => p.jobId === job.id);
              const shipped = marks.filter(p => p.currentStageId === 8).length;
              const pct = marks.length ? Math.round((shipped / marks.length) * 100) : 0;
              const isSelected = job.id === selectedJobId;
              return (
                <div
                  key={job.id}
                  onClick={() => setSelectedJobId(job.id)}
                  style={{
                    padding: '11px 14px',
                    borderBottom: '1px solid var(--b1)',
                    cursor: 'pointer',
                    background: isSelected ? 'var(--cyan-t)' : 'transparent',
                    transition: 'background .1s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{job.jobNumber}</div>
                    <StatusBadge status={job.status} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.projectName}</div>
                  <div style={{ fontSize: 11, color: 'var(--t4)' }}>{cust?.name || '—'} {job.dueDate && `· Due ${job.dueDate}`}</div>
                  {marks.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <div className="prog-track" style={{ height: 3 }}>
                        <div className="prog-fill" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--teal)' : 'var(--accent)' }} />
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--t4)', width: 28, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Job detail */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedJob ? (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <div style={{ textAlign: 'center', color: 'var(--t3)' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🏗️</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Select a job to view details</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Piece marks, assemblies, and stage progress appear here</div>
              </div>
            </div>
          ) : (
            <div className="card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Job header */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--b1)', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--t1)' }}>{selectedJob.jobNumber}</span>
                      <StatusBadge status={selectedJob.status} />
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 2 }}>{selectedJob.projectName}</div>
                    <div style={{ fontSize: 12, color: 'var(--t3)' }}>
                      {custById(selectedJob.customerId)?.name || ''}
                      {selectedJob.poNumber && ` · PO: ${selectedJob.poNumber}`}
                      {selectedJob.dueDate && ` · Due: ${selectedJob.dueDate}`}
                      {selectedJob.estTons && ` · ${selectedJob.estTons}T`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'add_piece_mark', data: { jobId: selectedJob.id, assemblyId: jobAssemblies[0]?.id } })}>
                      + Piece Mark
                    </button>
                  </div>
                </div>
                {/* Progress */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--t3)', width: 90 }}>{jobProgress}% complete</div>
                  <div className="prog-track" style={{ flex: 1, height: 5 }}>
                    <div className="prog-fill" style={{ width: `${jobProgress}%`, background: jobProgress === 100 ? 'var(--teal)' : 'var(--accent)', transition: 'width .4s' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--t3)', width: 60, textAlign: 'right' }}>{jobPieceMarks.filter(p => p.currentStageId === 8).length}/{jobPieceMarks.length} shipped</div>
                </div>
              </div>

              {/* Piece marks table */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {jobPieceMarks.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                    No piece marks yet —{' '}
                    <button className="auth-link" onClick={() => setModal({ type: 'add_piece_mark', data: { jobId: selectedJob.id, assemblyId: jobAssemblies[0]?.id } })}>
                      add the first piece mark
                    </button>
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Mark</th>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Spec</th>
                        <th>Stage</th>
                        <th>Work Center</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobPieceMarks.map(pm => {
                        const stage = fabStages.find(s => s.id === pm.currentStageId);
                        const wc    = workCenters.find(w => w.id === pm.workCenterId);
                        const isShipped = pm.currentStageId === 8;
                        const hasNext = pm.currentStageId < 8;
                        return (
                          <tr key={pm.id}>
                            <td><span className="mono" style={{ fontWeight: 700 }}>{pm.markNumber}</span></td>
                            <td><div style={{ fontSize: 13 }}>{pm.description || '—'}</div></td>
                            <td style={{ textAlign: 'center' }}>{pm.qty}</td>
                            <td><span className="bdg" style={{ background: 'var(--s2)', color: 'var(--t3)' }}>{pm.materialSpec}</span></td>
                            <td>
                              <span
                                className="stage-badge"
                                style={{ background: `${stageColor(pm.currentStageId)}20`, color: stageColor(pm.currentStageId) }}
                              >
                                <span style={{ fontSize: 7 }}>●</span>
                                {stage?.name || '—'}
                              </span>
                            </td>
                            <td style={{ fontSize: 12, color: 'var(--t3)' }}>{wc?.name || '—'}</td>
                            <td>
                              {hasNext && !isShipped && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setModal({ type: 'advance_stage', data: pm })}
                                >
                                  Advance →
                                </button>
                              )}
                              {isShipped && <span className="bdg" style={{ background: 'var(--teal-t)', color: 'var(--teal)' }}>✓ Shipped</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal?.type === 'add_job'        && <AddJobModal onClose={() => setModal(null)} />}
      {modal?.type === 'add_piece_mark' && <AddPieceMarkModal jobId={modal.data?.jobId} assemblyId={modal.data?.assemblyId} onClose={() => setModal(null)} />}
      {modal?.type === 'advance_stage'  && <AdvanceStageModal pieceMark={modal.data} onClose={() => setModal(null)} />}
    </div>
  );
}
